/**
 * Client-side filter helpers for Opportunities (loaded deals only).
 */

import { isDealLensRowAvailable } from "@/store/dealCore/dealAvailability";
import { getOpportunityOfferStatusPill } from "@/store/dealCore/dealOfferThreadUtils";

/** Default upper bound for purchase-price filter when deal set has no prices. */
export const DEFAULT_OPPORTUNITY_PRICE_MAX = 18000000;

/** @typedef {"all"|"pending"|"liked"|"passed"} OpportunityReviewStatus */

/**
 * @returns {Record<string, unknown>}
 */
export function createDefaultOpportunityFilters() {
  return {
    query: "",
    beds: "",
    baths: "",
    /** Offer-status pill label (same as card top-left), not raw DealLensStatus. */
    status: "",
    state: "",
    county: "",
    priceMin: 0,
    priceMax: DEFAULT_OPPORTUNITY_PRICE_MAX,
    /** Default matches prior “Pending Review” tab — avoid mounting all buckets + maps. */
    reviewStatus: "pending",
    /** Default "active" — hide deactivated deals (same as swipe deck). Use "" or "inactive" to include them. */
    availability: "active",
  };
}

/**
 * Deal is actionable when both the deal row and hydrated property are active.
 * Unhydrated deals stay available so enrichment lag does not false-positive.
 *
 * @param {object | null | undefined} deal
 * @returns {boolean}
 */
export function isOpportunityDealAvailable(deal) {
  return isDealLensRowAvailable(deal);
}

/**
 * List header label for the current review-status filter.
 * @param {OpportunityReviewStatus|string} [reviewStatus]
 * @returns {string}
 */
export function getOpportunityListTitle(reviewStatus = "pending") {
  switch (reviewStatus) {
    case "pending":
      return "Pending opportunities";
    case "liked":
      return "Liked opportunities";
    case "passed":
      return "Passed opportunities";
    default:
      return "All opportunities";
  }
}

/**
 * @param {OpportunityReviewStatus|string} reviewStatus
 * @returns {{ showPending: boolean, showLiked: boolean, showPassed: boolean }}
 */
export function reviewStatusToBucketFlags(reviewStatus) {
  switch (reviewStatus) {
    case "pending":
      return { showPending: true, showLiked: false, showPassed: false };
    case "liked":
      return { showPending: false, showLiked: true, showPassed: false };
    case "passed":
      return { showPending: false, showLiked: false, showPassed: true };
    default:
      return { showPending: true, showLiked: true, showPassed: true };
  }
}

/**
 * Purchase / listing price used for filter range (calculation first).
 * @param {object} deal
 * @returns {number}
 */
export function getDealFilterPrice(deal) {
  const fromCalc = Number(deal?.calculation?.listing_price);
  if (Number.isFinite(fromCalc) && fromCalc > 0) return fromCalc;
  const fromProperty = Number(deal?.propertyDetails?.additional_attributes?.listing_price);
  if (Number.isFinite(fromProperty) && fromProperty > 0) return fromProperty;
  return 0;
}

/**
 * Card top-left offer-status pill label used for filtering.
 * @param {object} deal
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null }} [viewerContext]
 * @returns {string}
 */
export function getOpportunityOfferStatusFilterLabel(deal, viewerContext = {}) {
  return getOpportunityOfferStatusPill(deal, viewerContext)?.label ?? "";
}

/**
 * Unique pill labels present in the loaded deal set (for the status dropdown).
 * @param {object[]} deals
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null }} [viewerContext]
 * @returns {string[]}
 */
export function collectOpportunityOfferStatusLabels(deals, viewerContext = {}) {
  if (!Array.isArray(deals)) return [];
  const labels = new Set();
  for (const deal of deals) {
    const label = getOpportunityOfferStatusFilterLabel(deal, viewerContext);
    if (label) labels.add(label);
  }
  return [...labels].sort((a, b) => a.localeCompare(b));
}

/**
 * @param {object} deal
 * @param {Record<string, unknown>} filters
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null }} [viewerContext]
 * @returns {boolean}
 */
export function dealMatchesOpportunityFilters(deal, filters, viewerContext = {}) {
  const query = String(filters.query || "")
    .trim()
    .toLowerCase();
  if (query) {
    const address = String(deal?.full_address || "").toLowerCase();
    const city = String(deal?.city || "").toLowerCase();
    if (!address.includes(query) && !city.includes(query)) return false;
  }

  if (filters.beds !== "" && filters.beds != null) {
    if (Number(deal?.bed) < Number(filters.beds)) return false;
  }
  if (filters.baths !== "" && filters.baths != null) {
    if (Number(deal?.bath) < Number(filters.baths)) return false;
  }

  if (filters.status) {
    if (getOpportunityOfferStatusFilterLabel(deal, viewerContext) !== String(filters.status)) return false;
  }
  if (filters.state) {
    if (String(deal?.state || "") !== String(filters.state)) return false;
  }
  if (filters.county) {
    if (String(deal?.county || "") !== String(filters.county)) return false;
  }

  const price = getDealFilterPrice(deal);
  const min = Number(filters.priceMin);
  const max = Number(filters.priceMax);
  if (Number.isFinite(min) && price < min) return false;
  if (Number.isFinite(max) && max > 0 && price > max) return false;

  if (filters.availability) {
    const available = isOpportunityDealAvailable(deal);
    if (filters.availability === "active" && !available) return false;
    if (filters.availability === "inactive" && available) return false;
  }

  return true;
}

/**
 * @param {object[]} deals
 * @param {Record<string, unknown>} filters
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null }} [viewerContext]
 * @returns {object[]}
 */
export function filterDealsByOpportunityFilters(deals, filters, viewerContext = {}) {
  if (!Array.isArray(deals)) return [];
  return deals.filter((deal) => dealMatchesOpportunityFilters(deal, filters, viewerContext));
}

/**
 * True when any clearable filter is active (excludes reviewStatus).
 * @param {Record<string, unknown>} filters
 * @param {{ minPrice?: number, maxPrice?: number }} [bounds]
 * @returns {boolean}
 */
export function hasActiveOpportunityFilters(filters, bounds = {}) {
  const minPrice = bounds.minPrice ?? 0;
  const maxPrice = bounds.maxPrice ?? DEFAULT_OPPORTUNITY_PRICE_MAX;
  if (String(filters.query || "").trim()) return true;
  if (filters.beds !== "" && filters.beds != null) return true;
  if (filters.baths !== "" && filters.baths != null) return true;
  if (filters.status) return true;
  if (filters.state) return true;
  if (filters.county) return true;
  if (Number(filters.priceMin) !== Number(minPrice) && Number(filters.priceMin) !== 0) return true;
  if (Number(filters.priceMax) !== Number(maxPrice) && Number(filters.priceMax) !== 0) return true;
  if (filters.availability && filters.availability !== "active") return true;
  return false;
}

/**
 * Advanced filters behind the Filter icon (excludes query, reviewStatus, deal status).
 * @param {Record<string, unknown>} filters
 * @param {{ minPrice?: number, maxPrice?: number }} [bounds]
 * @returns {number}
 */
export function countAdvancedOpportunityFilters(filters, bounds = {}) {
  const minPrice = bounds.minPrice ?? 0;
  const maxPrice = bounds.maxPrice ?? DEFAULT_OPPORTUNITY_PRICE_MAX;
  let count = 0;
  if (filters.beds !== "" && filters.beds != null) count += 1;
  if (filters.baths !== "" && filters.baths != null) count += 1;
  if (filters.state) count += 1;
  if (filters.county) count += 1;
  if (Number(filters.priceMin) !== Number(minPrice) && Number(filters.priceMin) !== 0) count += 1;
  if (Number(filters.priceMax) !== Number(maxPrice) && Number(filters.priceMax) !== 0) count += 1;
  return count;
}
